import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectLanguage, selectTranslations } from 'state/reducers/translation.reducers';
import BasicTable from 'components/BasicTable';
import { getActivities, getActivityDates, setActiveDates } from 'state/reducers/activity.reducers';
import {
  ActivityDashboardTableColumns,
  ActivityEnum,
  ActivityTableColumns,
  ActivityTypes,
} from 'constants/activity.constants';
import { columnType, FilterType, Order } from 'components/BasicTable/types';
import _ from 'lodash';
import { filterDates } from 'components/formElements/DateRange';
import { convertType } from 'constants/convert.constants';
import { getDay, timing } from 'constants/ui.constants';
import { Box } from '@mui/material';
import { getBeneficiaries } from 'state/reducers/beneficiary.reducers';
import { SupportType } from 'constants/contact.constants';
import { OrderBy } from 'constants/global.constants';

export default function ActivitiesTable() {
  const prefixFields = 'page.dashboard.recent_activity.';
  const t = useAppSelector(selectTranslations);
  const activities = useAppSelector(getActivities);
  const beneficiaries = _.keyBy(useAppSelector(getBeneficiaries), 'id');
  const lang = useAppSelector(selectLanguage);
  const activitiesColumns = () => {
    let columns: columnType[] = [];
    _.keys(ActivityDashboardTableColumns).forEach((element) => {
      let props: columnType = {
        fieldId: ActivityEnum.reference,
        name: element,
        title: t(prefixFields + 'column.' + element),
        type: ActivityTableColumns[element],
        formatDate: _.isEqual(ActivityEnum.settlementDate, element) ? timing.DATE : undefined,
      };
      columns.push(props);
    });
    return columns;
  };

  const formatValues = (list) => {
    let activities: any[] = [];
    list.forEach((item) => {
      let cl = _.clone(item);
      _.assign(cl, { status: t(prefixFields + 'status.' + _.snakeCase(item.status) + '.text') });
      _.assign(cl, { type: t(prefixFields + 'type.' + _.snakeCase(item.type) + '.text') });
      _.assign(cl, {
        updatedAt: [timing.TODAY, timing.YESTERDAY].includes(getDay(item.updatedAt) as timing)
          ? t('page.dashboard.timing.' + getDay(item.updatedAt) + '.text')
          : getDay(item.updatedAt),
      });
      if (_.isEqual(item.type, ActivityTypes.conversionActivities)) {
        try {
          _.assign(cl, {
            amount:
              item?.pendingQuote?.sellCurrency?.symbol +
              item?.pendingQuote?.altshulerSellAmount.toLocaleString('en-US') +
              ` ${'\u2192'} ` +
              item?.pendingQuote?.buyCurrency?.symbol +
              item?.pendingQuote?.altshulerBuyAmount.toLocaleString('en-US'),
          });
          _.assign(cl, { currencyCode: item?.pendingQuote?.sellCurrency?.code });
          let textConvert = _.replace(
            t(prefixFields + 'type.convert_from_to.text'),
            '{0}',
            item?.pendingQuote?.sellCurrency?.code
          );
          textConvert = _.replace(textConvert, '{1}', item?.pendingQuote?.buyCurrency?.code);
          _.assign(cl, { type: textConvert });
        } catch (e) { }
        activities.push(cl);
      } else if (_.isEqual(ActivityTypes.paymentFeesActivities, item.type)) {
        _.assign(cl, { amount: '-' + item?.currency?.symbol + item.amount.toLocaleString('en-US') });
        _.assign(cl, { currencyCode: item?.currency?.code });
        if (_.isEqual(item.type, ActivityTypes.paymentFeesActivities)) {
          _.assign(cl, { reference: null, status: null });
        }
        activities.push(cl);
      } else if ([ActivityTypes.paymentActivities].includes(item.type)) {
        _.assign(cl, { amount: '-' + item?.currency?.symbol + item.amount.toLocaleString('en-US') });
        _.assign(cl, { type: t(prefixFields + 'type.' + _.snakeCase(item.type) + '.text') });
        if (_.has(beneficiaries, item?.beneficiaryId)) {
          let bn = _.get(beneficiaries, item?.beneficiaryId).nickname;
          bn = _.replace(t(prefixFields + 'type.payment_to.text') + ' {1}', '{1}', bn);
          _.assign(cl, { type: bn });
        }
        activities.push(cl);
      } else {
        const isNegative = item?.balanceType === SupportType.Withdrawal;
        _.assign(cl, {
          amount:
            (isNegative ? '-' : '') + (item?.currency?.symbol || '') + item?.amount?.toLocaleString('en-US'),
        });
        _.assign(cl, { currencyCode: item?.currency?.code || '' });
        activities.push(cl);
      }
    });
    return activities;
  };

  return (
    <Box
      sx={{
        '& .MuiTableCell-root:last-child': {
          width: '100%',
          textAlign: _.includes(lang, 'he') ? 'start' : 'end',
          paddingRight: '8px',
          direction: _.includes(lang, 'he') ? 'rtl' : 'ltr'
        },
      }}
    >
      <BasicTable
        fieldId="id"
        hideHeader
        data={formatValues(activities)}
        columns={activitiesColumns()}
        perPage={5}
        noPagination
        noShrink
        tableClass="simple"
      />
    </Box>
  );
}
